/**
 * Badges & chips (§18). Status colour is centralised here via lib/status.ts so
 * every surface shows the same hue for the same meaning.
 *   - StatusChip  → order status, coloured by tone (the key scannability cue)
 *   - Badge       → neutral metadata (store region, carrier code…)
 *   - Tag         → small inline tag (order tags, "customs")
 *   - PriorityChip→ EXPRESS/priority marker (accent + bolt)
 */
import type { ReactNode } from "react";
import { statusTone, statusLabel } from "../../lib/status.js";
import { Icon } from "./Icon.js";

export function StatusChip({ status, title }: { status: string; title?: string }) {
  const tone = statusTone(status);
  return (
    <span className={`chip chip--${tone}`} title={title ?? statusLabel(status)}>
      <span className="chip__dot" aria-hidden="true" />
      {statusLabel(status)}
    </span>
  );
}

export function Badge({
  children,
  variant = "neutral",
  title,
}: {
  children: ReactNode;
  variant?: "neutral" | "outline";
  title?: string;
}) {
  return (
    <span className={`badge badge--${variant}`} title={title}>
      {children}
    </span>
  );
}

export function Tag({ children, tone }: { children: ReactNode; tone?: "warn" | "muted" }) {
  return <span className={`tag${tone ? ` tag--${tone}` : ""}`}>{children}</span>;
}

/** Boolean state as a tone chip (green on / grey off) — admin enabled/active etc. */
export function StateChip({
  on,
  onLabel = "Enabled",
  offLabel = "Disabled",
}: {
  on: boolean;
  onLabel?: string;
  offLabel?: string;
}) {
  return (
    <span className={`chip ${on ? "chip--ready" : "chip--cancelled"}`}>
      <span className="chip__dot" aria-hidden="true" />
      {on ? onLabel : offLabel}
    </span>
  );
}

export function PriorityChip({ label = "Express" }: { label?: string }) {
  return (
    <span className="chip chip--priority" title="Priority / express service">
      <Icon name="bolt" />
      {label}
    </span>
  );
}
